import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
// import Draggable from 'highcharts/modules/draggable-points';
// import Exportable from 'highcharts/modules/exporting';

import Highcharts from 'highcharts/highcharts-gantt';
import { DateTime } from 'scaleflow/utils/date-time';
import {
  statusBgColors,
  getColorFromInitiativeTitle,
} from 'scaleflow/utils/colorClasses';
import { INITIAITVE_SLIDEOVER_SAVE } from 'scaleflow/utils/custom-events';
import numberSort from 'scaleflow/utils/numberSort';
import capitalize from 'scaleflow/utils/capitalize';

// Draggable(Highcharts);
// Exportable(Highcharts);

export default class PlanningDashboardOverviewTimelineController extends Controller {
  @service('beta-toggle') betaService;
  @service organisation;
  @service store;
  @service router;
  @service intl;
  @service roadmap;

  @service initiativeSlideover;

  @tracked showFilterSlideover = false;
  @tracked selectedTimeRange = 0;

  queryParams = [
    'grouping',
    'statuses',
    'themes',
    'sources',
    'product_managers',
    'teams',
    'show_grouping',
    'start_date',
    'end_date',
    'columns',
  ];

  @tracked grouping = 'team.name';
  @tracked status = [];
  @tracked themes = [];
  @tracked sources = [];
  @tracked product_managers = [];
  @tracked teams = [];
  @tracked show_grouping = true;
  @tracked roadmaps = [];
  @tracked start_date = '';
  @tracked end_date = '';
  @tracked columns = [];

  chartInstance = null;
  defaultStatus = [];

  constructor() {
    super(...arguments);
    document.addEventListener(
      INITIAITVE_SLIDEOVER_SAVE,
      this.onInitiativeUpdate,
    );
    this.defaultStatus = this.intl._locale
      ? this.betaService?.statusOptions?.map((item) => item.value)
      : [];
  }

  get persons() {
    return this.store.peekAll('person');
  }

  get teamOptions() {
    return this.store.peekAll('team');
  }

  get roadmap_id() {
    return this.roadmap?.selected?.id;
  }

  @action
  onRender() {
    this.drawChart();
  }

  @tracked today = new Date();
  @tracked day = 1000 * 60 * 60 * 24;

  get groupingKey() {
    if (!this.grouping) return 'status';
    if (this.grouping.includes('theme')) return 'theme.title';
    if (this.grouping.includes('status')) return 'status';
    if (this.grouping.includes('product-manager')) return 'productManager.name';
    if (this.grouping.includes('team')) return 'team.name';
    if (this.grouping.includes('updates')) return 'latestUpdate';
    return 'source';
  }

  getGroupingLabel(initiative, groupKey) {
    let label = '';
    if (groupKey.toLowerCase() === 'status') {
      label = this.betaService.statusOptions.find(
        (item) => item.value === initiative.get(groupKey),
      ).label;
    } else if (groupKey.toLowerCase() === 'source') {
      label = this.betaService.sourceOptions.find(
        (item) => item.value === initiative.get(groupKey),
      ).label;
    } else if (groupKey.toLowerCase() === 'team.name') {
      label = initiative.teams_sync?.[0]?.name;
    } else if (groupKey === 'latestUpdate') {
      label = initiative?.latestUpdate?.statusLabel;
    } else label = initiative.get(groupKey);
    return label ? capitalize(label) : 'No group';
  }

  get initiatives() {
    const filteredInitiatives = this.roadmap.initiatives;
    const groupKey = this.groupingKey;
    const dataObject = filteredInitiatives.reduce((acc, item) => {
      if (!item.startDate || !item.source || !item.duration) return acc;

      const start_date = DateTime.from(item.startDate);
      const end_date = start_date.add(item.duration, 'weeks');
      const bon_value = item.bon_value
        ? Number(item.bon_value).toFixed(2)
        : '-';
      const accKey = this.getGroupingLabel(item, groupKey);
      const teams = item.teams_sync;
      let initiative = {
        id: item.id,
        title: item.title,
        start: start_date.valueOf(),
        end: end_date.valueOf(),
        status: item.status,
        status_color: statusBgColors[item.status],
        source: item.source,
        name: accKey ?? 'No group',
        avatar_color_class: getColorFromInitiativeTitle(item.title),
        abbv: item.title.substring(0, 1),
        duration: item.duration,
        bon: bon_value,
        person_weeks: item.person_weeks_including_uncertainty ?? '-',
        uncertainty: item.person_weeks_only_uncertainty ?? '-',
        team_id: teams?.[0]?.get('id'),
        team_name: teams?.[0]?.get('name'),
      };
      if (!acc[accKey])
        acc[accKey] = {
          data: [initiative],
          name: initiative.name,
        };
      else
        acc[accKey].data.push({
          ...initiative,
          parent: acc[accKey].data[0].id,
        });

      return acc;
    }, {});

    const sortedDataObject = Object.keys(dataObject)
      .sort((a, b) => {
        if (a === 'No group') return -Infinity;
        if (b === 'No group') return -Infinity;
        if (a.charAt(0).toUpperCase() > b.charAt(0).toUpperCase()) return -1;
        if (a.charAt(0).toUpperCase() < b.charAt(0).toUpperCase()) return 1;
        return 0;
      })
      .reduce((acc, key) => {
        acc[key] = dataObject[key];
        return acc;
      }, {});
    const arrayDataObject = Object.values(sortedDataObject);
    arrayDataObject.forEach((item) => {
      item.data = item.data
        .map((initiative, _index) => {
          return {
            ...initiative,
            ...(_index !== 0 && {
              parent: item.data[0].id,
              name: undefined,
            }),
          };
        })
        .sort((a, b) => numberSort(a.start, b.start, 'asc'));
    });
    return arrayDataObject;
  }

  generatePointDataFromInitiative(initiative) {
    const start_date = DateTime.from(initiative.startDate);
    const end_date = start_date.add(initiative.duration, 'weeks');
    const bon_value = initiative.bon_value
      ? Number(initiative.bon_value).toFixed(2)
      : '-';
    const teams = initiative.teams_sync;
    return {
      id: initiative.id,
      title: initiative.title,
      start: start_date.valueOf(),
      end: end_date.valueOf(),
      status: initiative.status,
      status_color: statusBgColors[initiative.status],
      source: initiative.source,
      // name: accKey ?? 'No group',
      avatar_color_class: getColorFromInitiativeTitle(initiative.title),
      abbv: initiative.title.substring(0, 1),
      duration: initiative.duration,
      bon: bon_value,
      person_weeks:
        initiative.person_weeks_including_uncertainty?.toFixed?.(2) ?? '-',
      uncertainty:
        initiative.person_weeks_only_uncertainty?.toFixed?.(2) ?? '-',
      team_id: teams[0]?.get?.('id'),
      team_name: teams[0]?.get?.('name'),
    };
  }

  @action
  updateInitiativePoint(newPoint, initiativeId) {
    const initiative = this.store.peekRecord('initiative', initiativeId);
    const starting_date = DateTime.from(initiative.startDate);
    const ending_date = starting_date.add(initiative.duration, 'weeks');

    const startDate = newPoint?.start
      ? DateTime.from(newPoint.start)
      : starting_date;
    const endDate = newPoint?.end ? DateTime.from(newPoint.end) : ending_date;

    const startDateString = startDate.format('YYYY-WW');
    const endDateString = endDate.format('YYYY-WW');
    const yearDifference =
      +endDateString.split('-')[0] - +startDateString.split('-')[0];
    const difference =
      yearDifference === 0
        ? +endDateString.split('-')[1] - +startDateString.split('-')[1]
        : 52 * yearDifference +
          +endDateString.split('-')[1] -
          +startDateString.split('-')[1];
    const duration = difference;

    initiative.duration = duration;
    initiative.startDate = startDate.format('YYYY-MM-DD');
    const accKey = this.getGroupingLabel(initiative, this.groupingKey);

    const serie = this.chartInstance.series.find(
      (serie) => serie.name === accKey,
    );
    const point = serie.data.find((item) => item.id === initiativeId);
    const update = this.generatePointDataFromInitiative(initiative);
    point?.update({ ...update });
  }

  get currentMonthStartEnd() {
    const now = DateTime.now().startOf('month');
    const one_month = now.add(1, 'months');
    return [now.valueOf(), one_month.valueOf()];
  }

  get currentQuarterStartEnd() {
    const startOfQuarter = DateTime.now().startOf('quarter');
    const endOfQuarter = DateTime.now().endOf('quarter');
    return [startOfQuarter.valueOf(), endOfQuarter.valueOf()];
  }

  @action
  async updateChartData() {
    await this.roadmap.initiatives;
    this.drawChart();
  }

  updateInitiative(initiativeId) {
    const initiative = this.store.peekRecord('initiative', initiativeId);
    initiative.save();
  }

  @action
  drawChart() {
    const that = this;
    if (this.chartInstance) this.chartInstance.destroy();
    this.chartInstance = new Highcharts.ganttChart('timeline-chart', {
      series: this.initiatives,
      chart: {
        plotBackgroundColor: 'rgb(249 250 251)',
        events: {
          load() {
            let chart = this;
            const [start, end] = [
              DateTime.from(that.roadmap.selected.start_date).valueOf(),
              DateTime.from(that.roadmap.selected.end_date).valueOf(),
            ];
            chart.xAxis[0].setExtremes(start, end);
          },
          redraw() {},
        },
        panning: {
          enabled: true,
          type: 'x',
        },
      },
      plotOptions: {
        gantt: {
          pointWidth: 50,
          zIndex: 899,
          cursor: 'pointer',
          borderRadius: 8,
          states: {
            hover: {
              enabled: false,
            },
            inactive: {
              eabled: false,
            },
          },
        },
        series: {
          pointPadding: 20,
          animation: false, // Do not animate dependency connectors
          enableMouseTracking: true,
          states: {
            hover: {
              enabled: false,
            },
            inactive: {
              enabled: false,
            },
          },
          dragDrop: {
            draggableX: true,
            liveRedraw: false,
            guideBox: {
              default: {
                className: 'highcharts-drag-box-default rounded-md',
                color: 'rgb(229 231 235)',
                cursor: 'move',
                lineColor: 'rgb(75 85 99)',
                lineWidth: 1,
                zIndex: 901,
              },
            },
            /* draggableY: true, */
            dragMinY: 0,
            dragMaxY: 2,
            dragPrecisionX: 1000 * 60 * 60 * 24 * 1, // Snap to 7 days
            // dragPrecisionX: 0, // Snap to 7 days
            dragHandle: {
              zIndex: 15,
              lineWidth: 3,
              pathFormatter: () =>
                'M 0 0 L 0 20 A 1 1 0 0 0 0 30 A 1 1 0 0 0 0 20 M 0 30 L 0 52'.split(
                  ' ',
                ),
            },
          },
          dataLabels: {
            enabled: true,
            format:
              '<div class="flex gap-x-2 gap-y-1 items-center highcharts-bar-label-container bg-white rounded pr-2 py-0.5">' +
              '<span style="color: #1F2937;" class="label">{point.title}</span><span class="text-gray-200">|</span>' +
              '<span class="text-sm text-gray-500 font-normal">{point.person_weeks}pw</span>' +
              '{#if point.uncertainty}<span class="text-sm text-gray-500 font-normal italic">(+{point.uncertainty})</span>{/if}' +
              '{#if point.team_id}<div class="w-6 h-6 shrink-0"><img src="https://api.dicebear.com/7.x/bottts/svg?seed={point.team_id}" class="w-full h-full bg-gray-200 rounded-full p-1"/></div>{/if}' +
              '</div>',
            useHTML: true,
            align: 'left',
            overflow: 'justify',
            inside: true,
            allowOverlap: false,
          },
          allowPointSelect: false,
          className: 'initiative-point',
          // point: {
          //   events: {
          //     select: (e) => this.onInitiativeSelect(e.target.id),
          //     unselect: () => console.log('on unselect'),
          //     remove: () => console.log('on remove'),
          //     drop: ({ newPoint, newPointId }) => {
          //       this.updateInitiativePoint(newPoint, newPointId);
          //       this.updateInitiative(newPointId);
          //     },
          //     // drag: ({ newPoint, newPointId }) => {
          //     //   // this.updateInitiativePoint(newPoint, newPointId);
          //     // },
          //   },
          // },
        },
      },
      title: {
        text: '',
      },
      navigator: {
        enabled: true,
        liveRedraw: true,
        adaptToUpdatedData: false,
        series: {
          type: 'gantt',
          pointPlacement: 0.5,
          pointPadding: 0.25,
          accessibility: {
            enabled: false,
          },
        },
      },

      scrollbar: {
        enabled: true,
      },
      accessibility: {
        enabled: false,
      },
      rangeSelector: {
        enabled: true,
        selected: that.selectedTimeRange,
        allButtonsEnabled: true,
        //customize range selector buttons
        buttons: [
          {
            type: 'month',
            text: 'Now',
            count: 3,
            events: {
              click: () => {
                const [start, end] = [
                  DateTime.from(this.roadmap.selected.start_date).valueOf(),
                  DateTime.from(that.roadmap.selected.end_date).valueOf(),
                ];
                this.chartInstance.xAxis[0].setExtremes(start, end);
                this.selectedTimeRange = 0;
                return false;
              },
            },
          },
          {
            type: 'month',
            text: '1m',
            count: 1,
            events: {
              click: () => {
                const [start, end] = this.currentMonthStartEnd;
                this.chartInstance.xAxis[0].setExtremes(start, end);
                this.selectedTimeRange = 1;
                return false;
              },
            },
          },
          {
            type: 'month',
            text: '3m',
            count: 3,
            events: {
              click: () => {
                const [start, end] = this.currentQuarterStartEnd;
                this.chartInstance.xAxis[0].setExtremes(start, end);
                this.selectedTimeRange = 2;
                return false;
              },
            },
          },
          {
            type: 'month',
            text: '6m',
            count: 6,
            events: {
              click: () => {
                this.chartInstance.xAxis[0].setExtremes(
                  DateTime.now().valueOf(),
                  DateTime.now().add(6, 'months').valueOf(),
                );
                this.selectedTimeRange = 3;
                return false;
              },
            },
          },
          {
            type: 'year',
            text: '1y',
            count: 1,
            events: {
              click: () => {
                this.chartInstance.xAxis[0].setExtremes(
                  DateTime.now().valueOf(),
                  DateTime.now().add(1, 'years').valueOf(),
                );
                this.selectedTimeRange = 4;
                return false;
              },
            },
          },
          {
            type: 'all',
            text: 'All',
          },
        ],
      },
      tooltip: {
        enabled: false,
        formatter: function () {
          // const source = this.point.options.source;
          const currency =
            that.organisation.locale === 'en-US' ? '&dollar;' : '&euro;';
          return (
            '<p class=""><span class="text-black text-sm font-semibold block mb-4">' +
            capitalize(this.point.options.source) +
            '</span>' +
            '<span class="text-gray-800 !text-sm capitalize font-normal block mb-2"><span class="text-gray-500">Duration:</span> ' +
            DateTime.from(this.point.options.start).format('DD MMM') +
            '-' +
            DateTime.from(this.point.options.end).format('DD MMM') +
            ', ' +
            this.point.options.duration +
            'w' +
            '</span>' +
            '<span class="text-gray-800 !text-sm capitalize font-normal block mb-2"><span class="text-gray-500">Person weeks:</span> ' +
            this.point.options.person_weeks +
            ' pw</span>' +
            '<span class="text-gray-800 !text-sm capitalize font-normal block mb-2"><span class="text-gray-500">Uncertainty:</span> ' +
            this.point.options.uncertainty +
            ' pw</span>' +
            '<span class="text-gray-800 !text-sm capitalize font-normal block mb-2"><span class="text-gray-500">Back-of-a-napkin:</span>' +
            currency +
            this.point.options.bon +
            '</span>' +
            '</p>'
          );
        },
        // shared: true,
        backgroundColor: 'rgba(255,255,255,1)',
        borderRadius: 4,
        borderColor: 'transparent',
        useHTML: true,
        animation: false,
        outside: true,
        shadow: true,
        followPointer: true,
      },
      lang: {
        accessibility: {
          axis: {
            xAxisDescriptionPlural:
              'The chart has a two-part X axis showing time in both week numbers and days.',
            yAxisDescriptionSingular:
              'The chart has a tabular Y axis showing a data table row for each point.',
          },
        },
      },
      // accessibility: {
      //   keyboardNavigation: {
      //     seriesNavigation: {
      //       mode: 'serialize',
      //     },
      //   },
      //   point: {
      //     // valueDescriptionFormat: 'Rented to {point.rentedTo} from {point.x:%A, %B %e} to {point.x2:%A, %B %e}.',
      //   },
      //   series: {
      //     descriptionFormatter: function (series) {
      //       return series.name + ', car ' + (series.index + 1) + ' of ' + series.chart.series.length + '.';
      //     },
      //   },
      // },
      xAxis: [
        //for second top bar
        {
          grid: {
            enabled: true,
            borderColor: '#ccd6eb',
          },
          startOfWeek: this.organisation?.first_day_of_week ?? 1,
          // startOnTick: true,
          gridLineWidth: 1,
          gridLineColor: '#ccd6eb',
          borderColor: '#ccd6eb',
          currentDateIndicator: {
            color: '#4f46e5',
            dashStyle: 'ShortDot',
            width: 2,
            label: {
              format: '%a, %b %e %Y', //find format docs here https://api.highcharts.com/class-reference/Highcharts.Time#dateFormat
            },
          },
          tickInterval: 24 * 3600 * 1000 * 7,
          max: DateTime.now().add(1, 'years').valueOf(),
          // events: {
          //   afterSetExtremes: (data) => {
          //     that.selectedRange = [data.min, data.max];
          //   },
          // },
          //   custom: {
          //     today,
          //     weekendPlotBands: true
          // }
        },
        //for top bar
        {
          grid: {
            enabled: true,
            borderColor: '#ccd6eb',
          },
        },
      ],
      yAxis: {
        labels: {
          enabled: true,
        },
        visible: this.show_grouping,
        grid: {
          enabled: true,
          borderColor: '#ccd6eb',
        },
        gridLineWidth: 0,
        staticScale: 54,
      },
      exporting: {
        enabled: false,
        // menuItemDefinitions: {
        //   toggleGrouping: {
        //     text: 'Show/Hide Grouping',
        //     onclick: function () {
        //       this.yAxis.forEach((axis) => {
        //         axis.update({
        //           visible: !axis.visible,
        //         });
        //       });
        //       that.show_grouping = !that.show_grouping;
        //     },
        //   },
        // },
        // buttons: {
        //   contextButton: {
        //     enabled: true,
        //     menuItems: ['toggleGrouping'],
        //   },
        // },
      },
    });
    var isSafari =
      /Safari/.test(navigator.userAgent) &&
      /Apple Computer/.test(navigator.vendor);
    if (isSafari) {
      document.querySelectorAll('.highcharts-point').forEach((element) => {
        element.classList.add('safari-stroke');
      });
    }
  }

  @action
  handleStatusSelect(e) {
    if (this.status.includes(e.value)) {
      this.status = this.status.filter((item) => item !== e.value);
    } else {
      const status = this.status;
      status.push(e.value);
      this.status = status;
    }
  }

  @action
  onInitiativeSelect(initiative_id) {
    this.initiativeSlideover.open(initiative_id);
  }

  @action
  transitionToCreateInitiative() {
    this.router.transitionTo(`/initiatives-create`);
  }

  @action
  toggleFilterSlideover(flag) {
    if (flag !== undefined && typeof flag === 'boolean')
      this.showFilterSlideover = flag;
    else this.showFilterSlideover = !this.showFilterSlideover;
  }

  @action
  onGroupingSelect(e) {
    this.grouping = e.value;
  }

  @action
  onStatusSelect(e) {
    this.status = e;
  }

  @action
  onThemeSelect(e) {
    this.themes = e;
  }

  @action
  onSourceSelect(e) {
    this.sources = e;
  }

  @action
  onPmSelect(e) {
    this.product_managers = e;
  }

  @action
  onTeamSelect(e) {
    this.teams = e;
  }

  @action
  onInitiativeUpdate({ detail: { initiative } }) {
    this.updateInitiativePoint(undefined, initiative);
  }

  willDestroy() {
    super.willDestroy(...arguments);
    document.removeEventListener(
      INITIAITVE_SLIDEOVER_SAVE,
      this.onInitiativeUpdate,
    );
  }

  @action
  handleFilterDelete(filter) {
    const param = filter.key;
    const filters = this[param];
    if (Array.isArray(filters))
      this[param] = filters.filter((item) => item !== filter.value);
    else this[param] = null;
  }

  @action
  onGroupingVisibilityToggle(e) {
    this.show_grouping = e;
    (this,
      this.chartInstance.yAxis.forEach((axis) => {
        axis.update({
          visible: !axis.visible,
        });
      }));
  }
}
